import { useState, useEffect } from 'react';
import {
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  HStack,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Text,
  Textarea,
  useColorModeValue,
  Button,
} from '@chakra-ui/react';
import { ExerciseDosage, TreatmentExercise } from 'types/models';

type VolumeUnit = 'reps' | 'seconds';

export interface ExerciseFormSubmitData {
  name: string;
  dosage: ExerciseDosage;
}

interface FormState {
  name: string;
  series: string;
  volumeValue: string;
  volumeUnit: VolumeUnit;
  load: string;
  restTime: string;
  notes: string;
}

const DEFAULTS: FormState = {
  name: '',
  series: '3',
  volumeValue: '10',
  volumeUnit: 'reps',
  load: '',
  restTime: '',
  notes: '',
};

const fromExercise = (ex: TreatmentExercise): FormState => ({
  name: ex.name,
  series: String(ex.dosage?.series ?? 3),
  volumeValue: String(ex.dosage?.repetitions ?? ex.dosage?.executionTime ?? 10),
  volumeUnit: ex.dosage?.executionTime != null ? 'seconds' : 'reps',
  load: ex.dosage?.load ?? '',
  restTime: ex.dosage?.restTime ?? '',
  notes: ex.dosage?.notes ?? '',
});

interface ExerciseFormProps {
  exercise?: TreatmentExercise;
  onSubmit: (data: ExerciseFormSubmitData) => void;
  isSaving?: boolean;
  submitBtnLabel?: string;
}

export default function ExerciseForm({ 
  exercise, 
  onSubmit, 
  isSaving = false,
  submitBtnLabel = 'Guardar' 
}: ExerciseFormProps) {
  const [form, setForm] = useState<FormState>(DEFAULTS);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const labelColor = useColorModeValue('gray.700', 'gray.200');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const inputBg    = useColorModeValue('gray.50',  'gray.700');

  useEffect(() => {
    setForm(exercise ? fromExercise(exercise) : DEFAULTS);
    setErrors({});
  }, [exercise]);

  const set = (field: keyof FormState, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim())      e.name        = 'Nombre requerido';
    if (!form.load.trim())      e.load        = 'Carga / resistencia requerida';
    if (!form.notes.trim())     e.notes       = 'Indicaciones técnicas requeridas';
    const vol = parseInt(form.volumeValue, 10);
    if (!vol || vol < 1)        e.volumeValue = 'Valor requerido';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildDosage = (): ExerciseDosage => ({
    series: Math.max(1, parseInt(form.series, 10) || 1),
    ...(form.volumeUnit === 'reps'
      ? { repetitions:   Math.max(1, parseInt(form.volumeValue, 10) || 1) }
      : { executionTime: Math.max(1, parseInt(form.volumeValue, 10) || 1) }),
    load: form.load.trim(),
    restTime: form.restTime.trim() || undefined,
    notes: form.notes.trim(),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    
    onSubmit({
      name: form.name.trim(),
      dosage: buildDosage(),
    });
  };

  return (
    <Flex as="form" id="exercise-form" onSubmit={handleSubmit} direction="column" gap="20px">
      {/* Nombre */}
      <FormControl isInvalid={!!errors.name}>
        <FormLabel fontSize="sm" fontWeight="600" color={labelColor} mb="6px">
          Nombre del ejercicio
          <Text as="span" fontWeight="400" color={mutedColor} ml="4px">(requerido)</Text>
        </FormLabel>
        <Input
          value={form.name}
          onChange={e => set('name', e.target.value)}
          placeholder="Ej. Puente glúteo..."
          bg={inputBg}
          borderRadius="10px"
          fontSize="sm"
        />
        {errors.name && <FormErrorMessage>{errors.name}</FormErrorMessage>}
      </FormControl>

      <Divider />

      {/* Series + Volumen */}
      <Grid templateColumns="1fr 1fr" gap="16px">
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="600" color={labelColor} mb="6px">Series</FormLabel>
          <NumberInput value={form.series} min={1} max={20} onChange={val => set('series', val)} clampValueOnBlur>
            <NumberInputField bg={inputBg} borderRadius="10px" fontSize="sm" />
            <NumberInputStepper>
              <NumberIncrementStepper /><NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </FormControl>

        <FormControl isInvalid={!!errors.volumeValue}>
          <FormLabel fontSize="sm" fontWeight="600" color={labelColor} mb="6px">
            {form.volumeUnit === 'reps' ? 'Repeticiones' : 'Tiempo (segundos)'}
          </FormLabel>
          <HStack spacing="8px">
            <NumberInput
              value={form.volumeValue}
              min={1}
              max={form.volumeUnit === 'reps' ? 200 : 3600}
              onChange={val => set('volumeValue', val)}
              clampValueOnBlur
              flex="1"
            >
              <NumberInputField bg={inputBg} borderRadius="10px" fontSize="sm" />
              <NumberInputStepper>
                <NumberIncrementStepper /><NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Select
              value={form.volumeUnit}
              onChange={e => set('volumeUnit', e.target.value as VolumeUnit)}
              bg={inputBg}
              borderRadius="10px"
              fontSize="sm"
              w="90px"
              flexShrink={0}
            >
              <option value="reps">Reps</option>
              <option value="seconds">Seg</option>
            </Select>
          </HStack>
          {errors.volumeValue && <FormErrorMessage>{errors.volumeValue}</FormErrorMessage>}
        </FormControl>
      </Grid>

      {/* Carga */}
      <FormControl isInvalid={!!errors.load}>
        <FormLabel fontSize="sm" fontWeight="600" color={labelColor} mb="6px">
          Carga / Resistencia
          <Text as="span" fontWeight="400" color={mutedColor} ml="4px">(requerido)</Text>
        </FormLabel>
        <Input
          value={form.load}
          onChange={e => set('load', e.target.value)}
          placeholder="Ej. Mancuerna de 2kg..."
          bg={inputBg}
          borderRadius="10px"
          fontSize="sm"
        />
        {errors.load && <FormErrorMessage>{errors.load}</FormErrorMessage>}
      </FormControl>

      {/* Descanso */}
      <FormControl>
        <FormLabel fontSize="sm" fontWeight="600" color={labelColor} mb="6px">
          Descanso entre series <Text as="span" fontWeight="400" color={mutedColor} ml="4px">(opcional)</Text>
        </FormLabel>
        <Input
          value={form.restTime}
          onChange={e => set('restTime', e.target.value)}
          placeholder="Ej. 60 segundos"
          bg={inputBg}
          borderRadius="10px"
          fontSize="sm"
        />
      </FormControl>

      <Divider />

      {/* Notas */}
      <FormControl isInvalid={!!errors.notes}>
        <FormLabel fontSize="sm" fontWeight="600" color={labelColor} mb="6px">
          Indicaciones técnicas <Text as="span" fontWeight="400" color={mutedColor} ml="4px">(requerido)</Text>
        </FormLabel>
        <Textarea
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          placeholder="Ej. Mantener espalda plana..."
          rows={3}
          bg={inputBg}
          borderRadius="10px"
          fontSize="sm"
          resize="vertical"
        />
        {errors.notes && <FormErrorMessage>{errors.notes}</FormErrorMessage>}
      </FormControl>
      
      {/* Botón invisible o nativo útil si necesitas gatillar el submit desde fuera mediante HTML5 form attribute */}
      <button type="submit" id="submit-hidden-btn" style={{ display: 'none' }} />
    </Flex>
  );
}